import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { ExamDayReservation, subscribeReservationBySessionAndExamDay } from '../../db/examday-reservation.js';
import type { ExamDay } from '../../db/examdays.js';

@customElement('ils-product-flow-header')
export class ILSProductFlowHeader extends ChemistryLitElement {
  @property() public readonly examDay!: ExamDay;

  @state() private readonly branding = new TranslationController(this);
  @state() private reservation: RXController<any, Maybe<ExamDayReservation>> = new RXController<
    any,
    Maybe<ExamDayReservation>
  >(this, undefined);
  @state() private remainingReservationTime = 0;

  private readonly timerInterval = window.setInterval(() => {
    if (!this.reservation.value) return;
    if (this.reservation.value.activeUntil.getTime() < Date.now()) return;
    this.remainingReservationTime = this.reservation.value.activeUntil.getTime() - Date.now();
  }, 1000);

  override connectedCallback(): void {
    super.connectedCallback();

    this.reservation = new RXController(
      this,
      subscribeReservationBySessionAndExamDay(this.examDay._id)(selectionSet(() => ExamDayReservation, 1)),
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  override render() {
    return html`
      <x-flex horizontal crossaxis-center w100 padding="sm">
        ${this._renderBackButton()} ${this._renderTitle()} ${this._renderReservationTimer()}
      </x-flex>
    `;
  }

  protected _renderBackButton(): Renderable {
    return html`
      <x-button
        mode="icon"
        icon="arrow_back"
        ${css({ alignSelf: 'flex-start' })}
        @click=${() => this.dispatchEvent(new CustomEvent('back', { bubbles: true, cancelable: true, composed: true }))}
      >
        <x-tooltip>
          <x-text> Zurück zur Prüfungsauswahl </x-text>
        </x-tooltip>
      </x-button>
    `;
  }

  protected _renderTitle(): Renderable {
    return html`
      <x-flex horizontal crossaxis-center center w100>
        <x-h3>
          ${this.branding.translate('product_type')}
          ${this.examDay.start.toLocaleString({
            ...DateTime.DATE_SHORT,
            day: '2-digit',
            month: '2-digit',
          })}
        </x-h3>
      </x-flex>
    `;
  }

  protected _renderReservationTimer(): Renderable {
    if (!this.reservation.value) return html`<div></div>`;
    const minutes = Math.floor(this.remainingReservationTime / 60000);
    const remainingSeconds = Math.floor((this.remainingReservationTime % 60000) / 1000);
    return html`
      <x-chip>
        <x-text>
          ${minutes}:${`${remainingSeconds}`.padStart(2, '0')}
          <x-tooltip>
            <x-text> Verbleibende Zeit der Reservierung </x-text>
          </x-tooltip>
        </x-text>
      </x-chip>
    `;
  }
}
